export const BOARD_IDS = {
  COIN: 1,
  STOCK: 2,
  NOTICE: 3,
  DIARY: 4,
  REALESTATE: 5,
  MACRO: 6,
  AI_INVEST: 7,
  TAX: 8,
  GLOBAL: 9,
  SIDEJOB: 10,
  FREE: 11,
  /** 운영/공지 — admin_role 보유자만 작성 가능 */
  NOTICE_OPS: 12,
  /** 건의/제안 — 일반 유저 작성 가능 */
  SUGGEST: 13,
  /** 이벤트 — 관리자만 작성, is_visible=FALSE (게시판 탭 숨김, /events 로만 노출) */
  EVENT: 14,
  /** 뉴스 — RSS 자동 수집 + claude -p 분류 (5종) */
  NEWS: 15,
  /** 시황 — 하루 4회 자동 발행 (모닝브리핑/코인24H/국장마감/미장마감) */
  MARKET: 16,
  /** 가이던스 분석 — guidance-bot 자동 게시 (회사 가이던스와 실적 정합성) */
  GUIDANCE_ANALYSIS: 17,
} as const;

/** 뉴스 카테고리 — sisegonggan.ai_news.category 와 정합
 *  공시(disclosure)는 별도 게시판(NOTICE)으로 노출하므로 뉴스 카테고리에서 제외 */
export const NEWS_CATEGORIES = [
  { key: 'kr_stock',     label: '국내증시' },
  { key: 'global_stock', label: '해외증시' },
  { key: 'coin',         label: '코인' },
  { key: 'macro',        label: '경제정책' },
] as const;
export type NewsCategoryKey = (typeof NEWS_CATEGORIES)[number]['key'];

/** 시황 카테고리 — sisegonggan.market_reports.category 와 정합 */
export const MARKET_REPORT_CATEGORIES = [
  { key: 'morning_brief', label: '모닝브리핑',  timeLabel: '08:00' },
  { key: 'coin_24h',      label: '코인 24H',    timeLabel: '09:10' },
  { key: 'intraday',      label: '장중 브리핑', timeLabel: '15:05' },
  { key: 'kr_close',      label: '국장마감',    timeLabel: '15:40' },
  { key: 'us_close',      label: '미장마감',    timeLabel: '04:10' },
] as const;
export type MarketReportCategoryKey = (typeof MARKET_REPORT_CATEGORIES)[number]['key'];

export type BoardId = (typeof BOARD_IDS)[keyof typeof BOARD_IDS];

export interface BoardMeta {
  id: BoardId;
  slug: string;
  name: string;
  /** @deprecated — 아이콘 사용 금지, 텍스트만. 타입 호환성을 위해 남김 */
  emoji?: string;
  description: string;
  isPremium?: boolean;
}

/**
 * 게시판 정책 (2026-04-17 이후 신규):
 *  - 시스템 게시판(코인시황/국내주식/공시뉴스 등)의 name / 자동생성 post title
 *    에 이모지 사용 절대 금지 (🚨 📢 🔥 등) — 텍스트만 사용
 *  - 신규 게시판 추가 시에도 name 필드에 이모지 포함 금지
 *  - BoardTabs, BoardPage, HomePage FEATURED_BOARDS 등 UI 좌측 아이콘도 금지
 *  - 유저가 직접 작성한 게시글/댓글 본문의 이모지는 허용 (본인 선택)
 *  - 한 페이지 20개 노출, cursor 기반 페이지네이션
 *  - 기본 정렬: 최신순 / 선택: 인기(hot_score 동적 계산)
 */
export const BOARDS: BoardMeta[] = [
  { id: 1,  slug: 'coin',       name: '코인',            description: '가상자산 실시간 이슈' },
  { id: 2,  slug: 'stock',      name: '주식게시판',       description: '국내·해외 주식 토론' },
  { id: 3,  slug: 'notice',     name: '공시 분석',       description: '금융 공시 분석 자동 수집' },
  { id: 4,  slug: 'diary',      name: '매매일지',        description: '수익/손실 매매일지', isPremium: true },
  { id: 5,  slug: 'realestate', name: '부동산',          description: '아파트·청약·임장' },
  { id: 6,  slug: 'macro',      name: '경제/거시지표',   description: '금리·환율·물가', isPremium: true },
  { id: 7,  slug: 'ai-invest',  name: 'AI/자동화투자',   description: '퀀트·알고리즘', isPremium: true },
  { id: 8,  slug: 'tax',        name: '절세/세금',       description: 'ISA·IRP·연금저축', isPremium: true },
  { id: 9,  slug: 'global',     name: '해외주식',        description: '미국주식·ETF' },
  { id: 10, slug: 'sidejob',    name: '부업/파이프라인',description: 'N잡·파이프라인' },
  { id: 11, slug: 'free',       name: '자유게시판',      description: '재테크 잡담' },
  { id: 12, slug: 'notice-ops', name: '운영/공지',       description: '운영 공지 및 패치노트' },
  { id: 13, slug: 'suggest',    name: '건의/제안',       description: '서비스 건의 및 제안' },
  { id: 14, slug: 'event',      name: '이벤트',          description: '운영 이벤트 공지' },
  { id: 15, slug: 'news',       name: '증시뉴스',        description: 'RSS 자동 수집 · 국내증시/해외증시/코인/경제정책/공시 분석' },
  { id: 16, slug: 'market',     name: '시장상황',        description: '오늘의 국장·미장·코인 마감 정리' },
  { id: 17, slug: 'guidance-analysis', name: '가이던스 분석', description: '회사 공시 가이던스와 분기 실적의 정합성을 AI가 분석' },
];

export const BOARD_BY_SLUG: Record<string, BoardMeta> = Object.fromEntries(
  BOARDS.map((b) => [b.slug, b]),
);
export const BOARD_BY_ID: Record<number, BoardMeta> = Object.fromEntries(
  BOARDS.map((b) => [b.id, b]),
);
